import { httpsCallable } from "firebase/functions";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db, functions, WORKSPACE_ID } from "./firebase";
import type { TagKind } from "./data";

/**
 * Almost every mutation goes through a callable function rather than a client
 * write. Inbox resolutions can merge people and change identity mappings, which
 * the Security Rules deliberately forbid from the client — the decision comes
 * from the user, but the write has to be made by the server so it can be
 * audited.
 *
 * The capture box at the bottom of this file is the one exception, and the
 * comment there says why: a note somebody writes to themselves decides nothing,
 * and it has to save in the moment it is typed rather than after a cold start.
 */

export async function resolveInboxItem(inboxItemId: string, resolution: string): Promise<void> {
  const fn = httpsCallable<{ inboxItemId: string; resolution: string }, { ok: boolean }>(
    functions,
    "resolveInboxItem",
  );
  await fn({ inboxItemId, resolution });
}

export type ContactTriageResult =
  | { status: "created"; personId: string }
  | { status: "linked"; personId: string }
  | { status: "dismissed" }
  | { status: "already_handled" }
  | { status: "ambiguous"; candidatePersonIds: string[] };

/**
 * Acts on an imported contact. `add` creates the person with the chosen tags;
 * `dismiss` closes the card for good and creates nothing.
 *
 * Distinct from `resolveInboxItem`, which only records a decision. That
 * distinction was invisible in the UI and produced the worst bug in the project
 * so far: sixty contacts classified, sixty cards closed, and not one person
 * created.
 */
export async function resolveContactTriage(
  inboxItemId: string,
  decision: "add" | "dismiss",
  tagIds: string[] = [],
  /** What the reviewer typed about this person, while looking at their name.
   *  Worth more than any tag, and there was nowhere to put it. */
  note = "",
): Promise<ContactTriageResult> {
  const fn = httpsCallable<
    { inboxItemId: string; decision: string; tagIds: string[]; note: string },
    ContactTriageResult
  >(functions, "resolveContactTriage");
  return (await fn({ inboxItemId, decision, tagIds, note })).data;
}

export interface SubmissionField {
  key: string;
  label: string;
  value: string;
  kind: "text" | "date" | "number" | "phone" | "list";
}

export interface SubmissionDetail {
  submissionId: string;
  eventType: string | null;
  occurredAt: string | null;
  receivedAt: string | null;
  page: string | null;
  form: string | null;
  title: string;
  fields: SubmissionField[];
}

/**
 * What somebody actually filled in on a form on the website.
 *
 * The submission has always been stored whole; it was never readable. A callable
 * rather than a Firestore read because `intake_events` holds the raw details of
 * every submission ever made in one collection — this answers for one id at a
 * time, which is all the screen needs.
 */
export async function getSubmission(submissionId: string): Promise<SubmissionDetail> {
  const fn = httpsCallable<{ submissionId: string }, SubmissionDetail>(
    functions,
    "getSubmission",
  );
  return (await fn({ submissionId })).data;
}

export interface PeopleExport {
  csv: string;
  people: number;
  withPhone: number;
  withEmail: number;
  generatedAt: string;
}

/**
 * The whole address book as a file for Excel.
 *
 * Admin only and audited on the server: this hands over every name, phone and
 * email in the community in one file that then lives outside the CRM.
 */
export async function exportPeople(): Promise<PeopleExport> {
  const fn = httpsCallable<void, PeopleExport>(functions, "exportPeople");
  return (await fn()).data;
}

/**
 * Hands a generated file to the browser.
 *
 * `text/csv` with an explicit `charset=utf-8`, and the server's leading BOM left
 * untouched — between them that is what makes Excel read Hebrew instead of
 * mojibake. The object URL is revoked afterwards so the blob is not held in
 * memory for the life of the tab.
 */
export function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function seedDemoData(): Promise<{ created: number; replaced: number }> {
  const fn = httpsCallable<void, { created: number; replaced: number }>(
    functions,
    "seedDemoData",
  );
  return (await fn()).data;
}

export async function clearDemoData(): Promise<{ deleted: number }> {
  const fn = httpsCallable<void, { deleted: number }>(functions, "clearDemoData");
  return (await fn()).data;
}

/** Creates a tag and returns its id, so a picker can select it at once. */
export async function createTagDef(name: string, kind: TagKind): Promise<string> {
  const fn = httpsCallable<{ name: string; kind: TagKind }, { id: string }>(
    functions,
    "createTagDef",
  );
  return (await fn({ name, kind })).data.id;
}

export async function updateTagDef(
  tagId: string,
  patch: { name?: string; status?: "active" | "hidden" },
): Promise<void> {
  const fn = httpsCallable<{ tagId: string } & typeof patch, { ok: boolean }>(
    functions,
    "updateTagDef",
  );
  await fn({ tagId, ...patch });
}

export async function seedTaxonomy(): Promise<{ created: number }> {
  const fn = httpsCallable<void, { created: number }>(functions, "seedTaxonomy");
  return (await fn()).data;
}

export interface TagDeleteResult {
  dryRun: boolean;
  tagName: string;
  people: number;
  sampleNames?: string[];
}

/**
 * Really deletes a tag — off every person carrying it, then the definition.
 *
 * Call with `dryRun` first and show the number: "delete tag" and "take this
 * label off 312 people" are the same action, and only one of them sounds like it.
 */
export async function deleteTagDef(tagId: string, dryRun = false): Promise<TagDeleteResult> {
  const fn = httpsCallable<{ tagId: string; dryRun: boolean }, TagDeleteResult>(
    functions,
    "deleteTagDef",
  );
  return (await fn({ tagId, dryRun })).data;
}

export interface TagMergeResult {
  dryRun: boolean;
  from: string;
  into: string;
  people: number;
}

/** Folds one tag into another, moving its people across first. */
export async function mergeTagDefs(
  fromTagId: string,
  intoTagId: string,
  dryRun = false,
): Promise<TagMergeResult> {
  const fn = httpsCallable<
    { fromTagId: string; intoTagId: string; dryRun: boolean },
    TagMergeResult
  >(functions, "mergeTagDefs");
  return (await fn({ fromTagId, intoTagId, dryRun })).data;
}

/** How many people carry each tag, by tag id. */
export async function tagUsageCounts(): Promise<Record<string, number>> {
  const fn = httpsCallable<void, { counts: Record<string, number> }>(functions, "tagUsageCounts");
  return (await fn()).data.counts;
}

export interface DeletePeopleResult {
  /** Agorot of giving history the blocked deletes would have unlinked. */
  blockedDonationTotalMinor?: number;
  donationTotalUnlinkedMinor?: number;
  requested: number;
  deleted: number;
  notFound: number;
  /** Blocked because the money attached to them is provider-verified. */
  blockedByVerifiedDonations: string[];
  donationsUnlinked: number;
  interactionsUnlinked: number;
  identityClaimsReleased: number;
}

/**
 * Deletes people. The documents really go; an `audit_log` entry keeps a full
 * snapshot of each one, so a mistake is recoverable from the record.
 *
 * A person with provider-verified donations is refused unless `force` is set —
 * deleting a donor must be a decision, not a side effect of tidying a list.
 */
export async function deletePeople(
  personIds: string[],
  force = false,
): Promise<DeletePeopleResult> {
  const fn = httpsCallable<{ personIds: string[]; force: boolean }, DeletePeopleResult>(
    functions,
    "deletePeople",
  );
  return (await fn({ personIds, force })).data;
}

/** Adds and removes tags on one person. */
export async function setPersonTags(
  personId: string,
  patch: { add?: string[]; remove?: string[] },
): Promise<void> {
  const fn = httpsCallable<{ personId: string } & typeof patch, { ok: boolean }>(
    functions,
    "setPersonTags",
  );
  await fn({ personId, ...patch });
}

export type InteractionType =
  | "call"
  | "whatsapp"
  | "meeting"
  | "visit"
  | "note"
  | "attendance"
  | "message";

export interface LogInteractionInput {
  personId: string;
  type: InteractionType;
  channel?: "phone" | "whatsapp" | "in_person" | "email" | "sms" | "other";
  direction?: "outbound" | "inbound" | "none";
  summary: string;
  details?: string;
  /** ISO instant. Defaults to now; a past date is fine, a future one is refused. */
  occurredAt?: string;
  /**
   * Whether this counts as real contact.
   *
   * A call that rang out is not contact, and recording it as one would tell the
   * engine this person has been attended to when nobody spoke to them. Defaults
   * to true for everything except a note.
   */
  meaningful?: boolean;
  /**
   * What still has to happen after this conversation.
   *
   * Without it the log records that a conversation took place and forgets what
   * was promised in it — "I spoke to his wife about teaching the boys for their
   * bar mitzvah" is not finished business. Becomes a task assigned to whoever
   * logged it, which is what Today reads.
   */
  followUp?: { dueAt: string; note?: string };
}

/**
 * Records that a conversation happened. The one thing the CRM asks a human to
 * type, and what the timeline and the recommendation engine are both built on.
 */
export async function logInteraction(input: LogInteractionInput): Promise<{
  interactionId: string;
  taskId: string | null;
  occurredAt: string;
  meaningful: boolean;
}> {
  const fn = httpsCallable<
    LogInteractionInput,
    {
      ok: boolean;
      interactionId: string;
      taskId: string | null;
      occurredAt: string;
      meaningful: boolean;
    }
  >(functions, "logInteraction");
  return (await fn(input)).data;
}

/** Marks a follow-up done. */
export interface CreateTaskInput {
  title: string;
  /** ISO instant. A reminder without a date is not a reminder. */
  dueAt: string;
  /** Optional: "order more candles" belongs to nobody. */
  personId?: string;
  description?: string;
}

/**
 * A reminder somebody decided on, rather than one the system inferred.
 *
 * Until now a follow-up could only appear as a by-product of logging a
 * conversation or of an enquiry arriving from the website.
 */
export async function createTask(input: CreateTaskInput): Promise<{ taskId: string }> {
  const fn = httpsCallable<CreateTaskInput, { taskId: string; dueAt: string }>(
    functions,
    "createTask",
  );
  return (await fn(input)).data;
}

export async function completeTask(taskId: string, note?: string): Promise<void> {
  const fn = httpsCallable<{ taskId: string; note?: string }, { ok: boolean }>(
    functions,
    "completeTask",
  );
  await fn({ taskId, note });
}

/** Moves a follow-up to a new date. A reminder without a date is not a reminder,
 *  so the new one is required rather than implied. */
export async function snoozeTask(taskId: string, dueAt: string): Promise<void> {
  const fn = httpsCallable<{ taskId: string; dueAt: string }, { ok: boolean }>(
    functions,
    "snoozeTask",
  );
  await fn({ taskId, dueAt });
}

/**
 * Closes an arriving service request.
 *
 * `done` is "dealt with"; `cancelled` is "should not have been a request". Kept
 * apart because merging them inflates every count of work completed by every
 * mistake ever filed.
 */
export async function resolveServiceRequest(
  serviceRequestId: string,
  resolution: "done" | "cancelled",
  note?: string,
): Promise<void> {
  const fn = httpsCallable<
    { serviceRequestId: string; resolution: string; note?: string },
    { ok: boolean }
  >(functions, "resolveServiceRequest");
  await fn({ serviceRequestId, resolution, note });
}

/**
 * Takes a follow-up off the list without claiming it was carried out.
 *
 * A reminder that should never have existed needs its own word, or the only way
 * to clear it is to lie about having done it. Soft: nobody can delete a task —
 * the Security Rules refuse it — so this closes the row rather than erasing it.
 */
export async function cancelTask(taskId: string, reason?: string): Promise<void> {
  const fn = httpsCallable<{ taskId: string; reason?: string }, { ok: boolean }>(
    functions,
    "cancelTask",
  );
  await fn({ taskId, reason });
}

/**
 * Says by hand that two people are, or are not, the same family.
 *
 * Relatives inside an imported address group are worked out from a shared
 * surname, and two unrelated families with the same surname in one building is
 * the case no rule can separate. Stored on both people and honoured by every
 * later recomputation.
 */
export async function setFamilyLink(
  personId: string,
  otherPersonId: string,
  related: boolean,
): Promise<{ names: { a: string; b: string } }> {
  const fn = httpsCallable<
    { personId: string; otherPersonId: string; related: boolean },
    { ok: boolean; related: boolean; names: { a: string; b: string } }
  >(functions, "setFamilyLink");
  return (await fn({ personId, otherPersonId, related })).data;
}

/** The people the CRM assigns work to. Slugs, so renaming never rewrites
 *  thousands of documents. */
/**
 * The two people the lists belong to.
 *
 * `greetingName` is what the Today screen calls them. It used to take the first
 * word of the Google account's display name, and the account is the Chabad
 * house's, so it said "בוקר טוב, חבד" to a man called מנחם מענדל.
 */
export const OWNERS: { slug: string; label: string; greetingName: string }[] = [
  { slug: "mendi", label: "מענדי", greetingName: "מנחם מענדל" },
  { slug: "shira", label: "שירה", greetingName: "שירה" },
];

export interface UpdatePersonInput {
  personId: string;
  displayName?: string;
  /** Raw values as typed. Normalisation and the identity claims are the
   *  server's job — a browser that edited these directly would change the
   *  display and leave the index pointing at the old number. */
  phones?: string[];
  emails?: string[];
  address?: {
    street?: string | null;
    houseNumber?: string | null;
    apartment?: string | null;
    city?: string | null;
    neighborhood?: string | null;
  } | null;
  owners?: string[];
  householdRole?: string | null;
  gender?: "male" | "female" | null;
  gregorianBirthDate?: string | null;
  organization?: string | null;
  education?: { school?: string | null; grade?: string | null } | null;
  note?: string;
}

/**
 * Edits a person. Rejects a phone number that already belongs to somebody else,
 * naming them — it is a typo or a duplicate, and the second is a merge.
 */
export async function updatePerson(input: UpdatePersonInput): Promise<void> {
  const fn = httpsCallable<UpdatePersonInput, { ok: boolean }>(functions, "updatePerson");
  await fn(input);
}

export interface MergePlan {
  keepId: string;
  dropId: string;
  keepName: string;
  dropName: string;
  gains: {
    phones: string[];
    emails: string[];
    tags: number;
    owners: string[];
    activities: number;
    donations: number;
    interactions: number;
    fillsFields: string[];
  };
  blockers: string[];
}

/** What a merge would do, computed from the same data the write uses. */
export async function previewMergePeople(
  keepPersonId: string,
  dropPersonId: string,
): Promise<MergePlan> {
  const fn = httpsCallable<{ keepPersonId: string; dropPersonId: string }, MergePlan>(
    functions,
    "previewMergePeople",
  );
  return (await fn({ keepPersonId, dropPersonId })).data;
}

export interface MergeResult {
  keepName: string;
  dropName: string;
  activitiesMoved: number;
  claimsRepointed: number;
  referencesRepointed: number;
  phonesAdded: number;
  emailsAdded: number;
  fieldsFilled: string[];
}

/**
 * Merges two records for one person. The record kept wins every conflict; the
 * other only fills gaps. A full snapshot of the losing document and every
 * reference goes to the audit log first.
 */
export async function mergePeople(
  keepPersonId: string,
  dropPersonIdOrIds: string | string[],
): Promise<MergeResult> {
  const fn = httpsCallable<
    { keepPersonId: string; dropPersonId?: string; dropPersonIds?: string[] },
    MergeResult
  >(functions, "mergePeople");
  const payload = Array.isArray(dropPersonIdOrIds)
    ? { keepPersonId, dropPersonIds: dropPersonIdOrIds }
    : { keepPersonId, dropPersonId: dropPersonIdOrIds };
  return (await fn(payload)).data;
}

export interface DuplicateRow {
  id: string;
  name: string;
  phones: string[];
  emails: string[];
  owners: string[];
  tagCount: number;
  householdName: string | null;
  householdId: string | null;
  householdRole: string | null;
  isChild: boolean;
  city: string | null;
  lastSeenDate: string | null;
  recordsMerged: number;
  notDuplicateOf: string[];
}

export type SharedIdentifierVerdict = "same_person" | "family" | "unclear";

export interface DuplicateCandidates {
  totalGroups: number;
  likelySamePerson: number;
  unclear: number;
  probablyFamily: number;
  assignedToShira?: number;
  phoneGroups: number;
  emailGroups: number;
  peopleInvolved: number;
  autoMergedCount?: number;
  staffDeletedCount?: number;
  groups: {
    identifier: string;
    kind: "phone" | "email";
    verdict: SharedIdentifierVerdict;
    sameHousehold: boolean;
    assignedTo?: string | null;
    people: DuplicateRow[];
    relatedPeople?: DuplicateRow[];
  }[];
}

/** Removes a pair or group from the suggestion queue without removing the ability to
 *  merge it later. Recorded on all records in the group. */
export async function dismissDuplicate(
  personId: string,
  otherPersonId: string,
): Promise<void> {
  const fn = httpsCallable<{ personId: string; otherPersonId: string }, { ok: boolean }>(
    functions,
    "dismissDuplicate",
  );
  await fn({ personId, otherPersonId });
}

export async function dismissDuplicateGroup(personIds: string[]): Promise<void> {
  const fn = httpsCallable<{ personIds: string[] }, { ok: boolean }>(
    functions,
    "dismissDuplicate",
  );
  await fn({ personIds });
}

/** Assigns a duplicate/family group to Shira (or Mendi) and creates an open task for her. */
export async function assignDuplicateGroup(
  personIds: string[],
  assignToOwner: "shira" | "mendi",
): Promise<void> {
  const fn = httpsCallable<
    { personIds: string[]; assignToOwner: "shira" | "mendi" },
    { ok: boolean; assignedTo?: string }
  >(functions, "dismissDuplicate");
  await fn({ personIds, assignToOwner });
}

/** Links people into a family household with explicit roles (father/mother/child/spouse) and auto-merges duplicate parents/children. */
export async function linkDuplicateAsFamily(
  personIds: string[],
  roles: Record<string, string>,
): Promise<{ ok: boolean; householdName?: string; mergedIds?: string[] }> {
  const fn = httpsCallable<
    { personIds: string[]; roles: Record<string, string> },
    { ok: boolean; householdName?: string; mergedIds?: string[] }
  >(functions, "dismissDuplicate");
  return (await fn({ personIds, roles })).data;
}

export async function duplicateCandidates(limit = 60): Promise<DuplicateCandidates> {
  const fn = httpsCallable<{ limit: number }, DuplicateCandidates>(
    functions,
    "duplicateCandidates",
  );
  return (await fn({ limit })).data;
}

/**
 * The six things you can do with a recommendation (spec §4.1).
 *
 * `contacted` is deliberately not `done`. Tapping WhatsApp opens a chat; it is
 * not evidence a message was sent, and treating it as contact would move the
 * person's recency on the strength of a tab opening.
 */
export type RecommendationAction =
  | "contacted"
  | "done"
  | "later"
  | "not_relevant"
  | "reassign"
  | "did_not_happen"
  | "archive"
  | "restore";

export const DISMISS_REASONS: { id: string; label: string; hint: string }[] = [
  { id: "not_now", label: "לא עכשיו", hint: "נציע שוב בעוד חודש" },
  { id: "already_handled", label: "כבר טופל", hint: "לא נציע שוב במשך שבועיים" },
  { id: "wrong_person", label: "לא איש הקשר הנכון", hint: "לא נציע שוב במשך שנה" },
  { id: "do_not_contact", label: "לא ליצור קשר", hint: "הוא לא רוצה שיפנו אליו" },
  { id: "other", label: "אחר", hint: "לא נציע שוב במשך חודשיים" },
];

export interface ActOnRecommendationInput {
  recommendationId: string;
  action: RecommendationAction;
  channel?: "whatsapp" | "phone" | "in_person";
  /** Required for `not_relevant` — one of DISMISS_REASONS. */
  reason?: string;
  note?: string;
  /** Required for `later`. ISO instant. */
  laterDate?: string;
  /** Required for `reassign`. */
  assignTo?: string;
}

export async function actOnRecommendation(
  input: ActOnRecommendationInput,
): Promise<{ status: string; nextReviewAt?: string; suppressedUntil?: string }> {
  const fn = httpsCallable<
    ActOnRecommendationInput,
    { ok: boolean; status: string; nextReviewAt?: string; suppressedUntil?: string }
  >(functions, "actOnRecommendation");
  return (await fn(input)).data;
}

export interface ComputeRecommendationsResult {
  dayKey: string;
  ruleVersion: number;
  signalCounts: Record<string, number>;
  candidates: number;
  perOwner: Record<string, number>;
  created: number;
  keptFromEarlierRun: number;
  retired: number;
}

/**
 * Rebuilds today's list now. Safe to press — the engine keeps every card already
 * on the list and never revives one that has been acted on, so this fills gaps
 * rather than throwing away the morning's decisions.
 */
/** "עוד משימות": more cards on today's list for one owner. */
export async function extendTodayList(
  owner: string,
  by = 5,
): Promise<{ added: number; atCeiling: boolean }> {
  const fn = httpsCallable<{ owner: string; by: number }, { added: number; atCeiling: boolean }>(
    functions,
    "extendTodayList",
  );
  return (await fn({ owner, by })).data;
}

export async function computeRecommendationsNow(): Promise<ComputeRecommendationsResult> {
  const fn = httpsCallable<void, ComputeRecommendationsResult>(
    functions,
    "computeRecommendationsNow",
  );
  return (await fn()).data;
}

export interface DedupeTimelineResult {
  dryRun: boolean;
  activitiesScanned: number;
  duplicateGroups: number;
  /** What the pass intends to collapse. */
  entriesToRemove: number;
  /** What it actually collapsed. Lower than the above means something failed. */
  entriesRemoved: number;
  peopleAffected: number;
  largestGroup: number;
  interactionsMarked: number;
  groupsMerged: number;
  failedGroups: number;
  errors: string[];
  /** A few real groups, each joined into one line. Flat because the same object
   *  is written to `audit_log`, and Firestore rejects nested arrays. */
  examples: string[];
}

/**
 * Collapses the timeline to one entry per event.
 *
 * The export recorded each real event once per source system, so around two
 * fifths of the timeline was doubled. Only the projection is collapsed — every
 * source interaction is kept, marked rather than deleted.
 */
export async function dedupeTimeline(dryRun = false): Promise<DedupeTimelineResult> {
  const fn = httpsCallable<{ dryRun: boolean }, DedupeTimelineResult>(functions, "dedupeTimeline");
  return (await fn({ dryRun })).data;
}

/** Donations that look like one payment recorded twice. Reported, never merged —
 *  a real double charge and an export artefact are indistinguishable from here. */
export async function findDuplicateDonations(): Promise<{
  donationsScanned: number;
  suspectGroups: number;
  groups: { id: string; name: string; purpose: string; amount: number | null }[][];
}> {
  const fn = httpsCallable<void, {
    donationsScanned: number;
    suspectGroups: number;
    groups: { id: string; name: string; purpose: string; amount: number | null }[][];
  }>(functions, "findDuplicateDonations");
  return (await fn()).data;
}

export interface BackfillFamiliesResult {
  dryRun: boolean;
  people: number;
  households: number;
  /** Groupings that really are one family. */
  realFamilies: number;
  /** Groupings that turned out to be a street address holding several families. */
  addressGroups: number;
  largestFamily: number;
  largestAddressGroup: number;
  coupleRecords: number;
  peopleSharingAPhone: number;
  peopleWithoutHousehold: number;
  peopleWithNoResolvedFamily: number;
}

/**
 * Derives what a single-document import could not: household membership, whether
 * a "household" is a family or a building, which records name a couple, and
 * which phone numbers are shared. Rerunnable — everything it writes is derived.
 */
export async function backfillFamilies(dryRun = false): Promise<BackfillFamiliesResult> {
  const fn = httpsCallable<{ dryRun: boolean }, BackfillFamiliesResult>(
    functions,
    "backfillFamilies",
  );
  return (await fn({ dryRun })).data;
}

/**
 * Google Contacts is read-only and one-way. These three calls are the whole
 * surface: authorise an account, run a sync now, revoke.
 */
export async function startGoogleContactsConnect(
  withCalendar = false,
): Promise<{ url: string; redirectUri: string }> {
  const fn = httpsCallable<{ withCalendar: boolean }, { url: string; redirectUri: string }>(
    functions,
    "startGoogleContactsConnect",
  );
  return (await fn({ withCalendar })).data;
}

/**
 * Pushes the events that already exist into Google Calendar.
 *
 * One-way: the CRM is the source of truth and nothing is ever read back. The
 * trigger only sees new writes, so this is what makes the events already in the
 * system appear after the calendar is first connected.
 */
export async function resyncEventsToCalendar(input: {
  fromDate?: string;
  limit?: number;
} = {}): Promise<{ scanned: number; counts: Record<string, number>; errors: string[] }> {
  const fn = httpsCallable<
    typeof input,
    { scanned: number; counts: Record<string, number>; errors: string[] }
  >(functions, "resyncEventsToCalendar");
  return (await fn(input)).data;
}

export interface SyncContactsResult {
  scanned: number;
  offered: number;
  alreadyKnown: number;
  skipped: number;
  duplicates: number;
  fullResync: boolean;
}

export async function syncGoogleContactsNow(): Promise<SyncContactsResult> {
  const fn = httpsCallable<void, SyncContactsResult>(functions, "syncGoogleContactsNow");
  return (await fn()).data;
}

export async function disconnectGoogleContacts(): Promise<void> {
  const fn = httpsCallable<void, { ok: boolean }>(functions, "disconnectGoogleContacts");
  await fn();
}

export interface ResetResult {
  total: number;
  deletedByCollection: Record<string, number>;
}

/**
 * Clears imported and derived data. Requires the typed confirmation the server
 * demands — a single tap must not be able to empty the database.
 */
export async function resetImportedData(): Promise<ResetResult> {
  const fn = httpsCallable<{ confirm: string }, ResetResult>(functions, "resetImportedData");
  return (await fn({ confirm: "מוחק הכל" })).data;
}

export interface ImportClaimInput {
  personId: string;
  householdRole: string | null;
  isChild: boolean;
  phones: string[];
  recordsMerged: number;
  hasEmail: boolean;
}

export interface ImportPrepareResult {
  runId: string;
  dryRun: boolean;
  people: number;
  distinctPhones: number;
  sharedPhones: number;
  peopleHoldingUnclaimedPhone: number;
  peopleWithNoIdentifier: number;
  vocabulary: {
    personTags: number;
    events: number;
    businessServices: number;
    donationLabels: number;
    fragmentsIgnored: number;
  };
  fragments: string[];
  counts: Record<string, number>;
}

export async function importPrepare(input: {
  dryRun: boolean;
  claimInputs: ImportClaimInput[];
  labels: string[];
  counts: Record<string, number>;
}): Promise<ImportPrepareResult> {
  const fn = httpsCallable<typeof input, ImportPrepareResult>(functions, "importPrepare");
  return (await fn(input)).data;
}

export interface ImportBatchResult {
  written: number;
  skipped: number;
  skippedReasons: Record<string, number>;
}

export async function importBatch(
  runId: string,
  kind: "people" | "households" | "engagements" | "donations",
  records: unknown[],
): Promise<ImportBatchResult> {
  const fn = httpsCallable<
    { runId: string; kind: string; records: unknown[] },
    ImportBatchResult
  >(functions, "importBatch");
  return (await fn({ runId, kind, records })).data;
}

export interface KodeshboardCounts {
  scanned: number;
  imported: number;
  matchedExistingPerson: number;
  createdPerson: number;
  skipped: number;
  skippedReasons: Record<string, number>;
  ambiguous: number;
  needsReview?: number;
}

export interface KodeshboardSyncResult {
  dryRun: boolean;
  memorials: KodeshboardCounts;
  birthdays: KodeshboardCounts;
}

/**
 * Pulls memorials from KodeshBoard. `dryRun` reports what would happen without
 * writing — worth using on a first pass over real synagogue records.
 */
export async function syncKodeshboardNow(dryRun = false): Promise<KodeshboardSyncResult> {
  const fn = httpsCallable<{ dryRun: boolean }, KodeshboardSyncResult>(
    functions,
    "syncKodeshboardNow",
  );
  return (await fn({ dryRun })).data;
}

export type CreatePersonResult =
  | { status: "created"; personId: string }
  | { status: "existing"; personId: string; matchedBy: "phone" | "email" | "external_ref" }
  | { status: "ambiguous"; candidatePersonIds: string[] };

/**
 * Creating a person is a callable rather than a client write because a person
 * is a document *plus* a claim on the identity index. Writing the document from
 * the browser would claim nothing, and the next form submission carrying the
 * same phone number would create a duplicate.
 */
export async function createPerson(input: {
  fullName: string;
  phone?: string;
  email?: string;
  note?: string;
  tagIds?: string[];
}): Promise<CreatePersonResult> {
  const fn = httpsCallable<typeof input, CreatePersonResult>(functions, "createPerson");
  return (await fn(input)).data;
}

export interface EventInput {
  title: string;
  /** The join key the website's registration forms post against. Derived from
   *  the title when left out. */
  slug?: string;
  description?: string;
  /** ISO instant. */
  startAt: string;
  endAt?: string | null;
  allDay?: boolean;
  locationName?: string;
  locationAddress?: string;
  status?: "draft" | "published" | "done" | "cancelled";
  owners?: string[];
  capacity?: number | null;
  registrationOpen?: boolean;
  audienceTagIds?: string[];
  /** Present only on create. Changing when a series happens means a new series,
   *  because the date is per-occurrence and cannot be pushed down. */
  recurrence?: Recurrence | null;
}

/**
 * Creates an event, and attaches any registrations already waiting for it.
 *
 * That second half is the normal case rather than the exception: the website's
 * form goes live before anybody opens the CRM, so sign-ups arrive holding a page
 * slug and nothing to attach to until the event exists.
 */
export async function createEvent(input: EventInput): Promise<{
  eventId: string;
  slug: string;
  registrationsLinked: number;
  occurrencesCreated: number;
  recurrenceText: string | null;
}> {
  const fn = httpsCallable<
    EventInput,
    {
      eventId: string;
      slug: string;
      registrationsLinked: number;
      occurrencesCreated: number;
      recurrenceText: string | null;
    }
  >(functions, "createEvent");
  return (await fn(input)).data;
}

export async function updateEvent(
  input: Partial<EventInput> & { eventId: string },
): Promise<{ registrationsLinked: number }> {
  const fn = httpsCallable<typeof input, { ok: boolean; registrationsLinked: number }>(
    functions,
    "updateEvent",
  );
  return (await fn(input)).data;
}

/** Cancelled, not deleted: an event people were told about is a fact, and
 *  registrations point at it. */
/**
 * Deletes an event outright, rather than cancelling it.
 *
 * Registrations are unlinked and kept — somebody filled in that form whatever we
 * do with the event, and leaving `eventSlug` in place means re-creating the event
 * re-adopts them. Presence observations for the event are removed with it.
 */
export async function deleteEvent(
  eventId: string,
  scope: "one" | "series" = "one",
): Promise<{
  eventsDeleted: number;
  unlinkedRegistrations: number;
  removedObservations: number;
}> {
  const fn = httpsCallable<
    { eventId: string; scope: string },
    { eventsDeleted: number; unlinkedRegistrations: number; removedObservations: number }
  >(functions, "deleteEvent");
  return (await fn({ eventId, scope })).data;
}

export async function cancelEvent(eventId: string, reason?: string): Promise<void> {
  const fn = httpsCallable<{ eventId: string; reason?: string }, { ok: boolean }>(
    functions,
    "cancelEvent",
  );
  await fn({ eventId, reason });
}

/**
 * Marks who turned up.
 *
 * Also writes each person's timeline and moves their contact recency — being
 * seen at a Shabbat meal is contact, and it is exactly the signal the
 * recommendation engine has almost none of.
 */
export async function markAttendance(
  eventId: string,
  registrationIds: string[],
  attended = true,
): Promise<{ marked: number; timelined: number }> {
  const fn = httpsCallable<
    { eventId: string; registrationIds: string[]; attended: boolean },
    { ok: boolean; marked: number; timelined: number }
  >(functions, "markAttendance");
  return (await fn({ eventId, registrationIds, attended })).data;
}

export interface LinkRegistrationsResult {
  registrations: number;
  linked: number;
  alreadyLinked: number;
  /** Slugs with sign-ups and no event. Each one is somebody waiting on a form
   *  nobody has read. */
  unmatchedSlugs: Record<string, number>;
  noSlug: number;
}

/**
 * Clears the pile of Google contact cards left by the first sync.
 *
 * Contacts are never re-offered — a card id is derived from the Google
 * resourceName — so this is a broom for the backlog, not a change to the sync.
 * With `beforeIso` the cards that arrived since are left alone.
 */
export async function dismissContactBacklog(input: {
  beforeIso?: string;
  dryRun?: boolean;
}): Promise<{
  dryRun: boolean;
  dismissed?: number;
  wouldDismiss?: number;
  keptNewer?: number;
  more?: boolean;
}> {
  const fn = httpsCallable<typeof input, {
    dryRun: boolean;
    dismissed?: number;
    wouldDismiss?: number;
    keptNewer?: number;
    more?: boolean;
  }>(functions, "dismissContactBacklog");
  return (await fn(input)).data;
}

/** Midnight this morning, Jerusalem, as an instant. The cutoff for "old". */
export function startOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export async function linkRegistrations(): Promise<LinkRegistrationsResult> {
  const fn = httpsCallable<void, LinkRegistrationsResult>(functions, "linkRegistrations");
  return (await fn()).data;
}

/**
 * Presence — who was actually at an event.
 *
 * Spec §4.6. A ticked name means somebody saw that person; an unticked name
 * means nothing, which is why `coverage` travels with every observation and only
 * `complete` may ever support an inference about who stopped coming.
 *
 * Marking hangs off a real event, not off a fixed list of gathering kinds. The
 * first version asked "shacharit or mincha or a class?" from a hardcoded list,
 * which the product owner rejected as both inconvenient and a second calendar
 * competing with the real one. A daily minyan he wants tracked is a recurring
 * event.
 */
export interface RosterPerson {
  id: string;
  name: string;
  /** How many earlier occurrences of this series they were seen at. */
  times: number;
  lastIso: string;
  isChild: boolean;
  /** Signed up for this event, so they said they were coming. */
  registered: boolean;
}

export interface PresenceRosterResult {
  event: {
    id: string;
    title: string;
    startIso: string;
    startDate: string;
    hebrewLabel: string | null;
    seriesId: string | null;
  };
  /** Why the roster looks the way it does, so an empty one can be explained
   *  rather than just looking broken. */
  rosterSource: "registered" | "series" | "none";
  /** Earlier markings of this series the roster was learned from. */
  observations: number;
  roster: RosterPerson[];
  /** What is already marked for this event, so reopening the screen shows the
   *  existing list rather than a blank grid. */
  today: {
    observationId: string;
    participantIds: string[];
    coverage: string;
    unknownCount: number;
  } | null;
}

export async function presenceRoster(eventId: string): Promise<PresenceRosterResult> {
  const fn = httpsCallable<{ eventId: string }, PresenceRosterResult>(
    functions,
    "presenceRoster",
  );
  return (await fn({ eventId })).data;
}

export interface RecordPresenceInput {
  eventId: string;
  participantIds: string[];
  coverage: "complete" | "partial" | "unknown";
  /** People present who are not in the CRM. Counted, deliberately not named —
   *  guessing at a name is how a duplicate gets created. */
  unknownCount?: number;
  note?: string;
}

export interface RecordPresenceResult {
  observationId: string;
  eventId: string;
  eventTitle: string;
  present: number;
  added: number;
  removed: number;
  skippedMissingPeople: number;
  /** True when this corrected an existing observation rather than creating one. */
  isUpdate: boolean;
}

export async function recordPresence(
  input: RecordPresenceInput,
): Promise<RecordPresenceResult> {
  const fn = httpsCallable<RecordPresenceInput, RecordPresenceResult>(
    functions,
    "recordPresence",
  );
  return (await fn(input)).data;
}

/**
 * A recurrence rule.
 *
 * Occurrences are materialised as real events rather than expanded from the rule
 * at read time, because each one carries its own registrations and attendance —
 * every Shabbat meal has its own guests. A calendar can expand a rule; a CRM
 * needs a document to sign up to.
 */
export interface Recurrence {
  freq: "weekly" | "biweekly" | "monthly" | "yearly";
  /** 0 = Sunday. Weekly and biweekly only; empty means the start's own weekday. */
  byWeekday?: number[];
  /** One or the other, never both — whichever came first would be a guess. */
  untilDate?: string | null;
  count?: number | null;
}

export async function cancelEventSeries(
  seriesId: string,
  reason?: string,
  fromDate?: string,
): Promise<{ cancelled: number; fromDate: string }> {
  const fn = httpsCallable<
    { seriesId: string; reason?: string; fromDate?: string },
    { cancelled: number; fromDate: string }
  >(functions, "cancelEventSeries");
  return (await fn({ seriesId, reason, fromDate })).data;
}

/**
 * Moves a whole series onto different days of the week.
 *
 * Future meetings are regenerated on the new day. The past is untouched, and so
 * is any meeting that already has sign-ups or that somebody moved on purpose.
 */
export async function rescheduleEventSeries(
  seriesId: string,
  byWeekday: number[],
): Promise<{
  removed: number;
  created: number;
  keptPast: number;
  keptDetached: number;
  keptBooked: number;
}> {
  const fn = httpsCallable<
    { seriesId: string; byWeekday: number[] },
    { removed: number; created: number; keptPast: number; keptDetached: number; keptBooked: number }
  >(functions, "rescheduleEventSeries");
  return (await fn({ seriesId, byWeekday })).data;
}

/** Applies an edit to future occurrences. Skips any occurrence somebody has
 *  already edited on its own — that is what `detached` is for. */
export async function updateEventSeries(
  input: Partial<EventInput> & { seriesId: string },
): Promise<{ updated: number; skippedDetached: number }> {
  const fn = httpsCallable<typeof input, { updated: number; skippedDetached: number }>(
    functions,
    "updateEventSeries",
  );
  return (await fn(input)).data;
}

/**
 * Throws away a chosen set of contact cards at once.
 *
 * Different from `dismissContactBacklog`, which clears everything before a date.
 * That answers "start again"; this answers "these ones, not those" — which is
 * what filtering a list of names actually is.
 */
export async function dismissContacts(inboxItemIds: string[]): Promise<{
  dismissed: number;
  alreadyHandled: number;
  notFound: number;
  wrongType: number;
}> {
  const fn = httpsCallable<
    { inboxItemIds: string[] },
    { dismissed: number; alreadyHandled: number; notFound: number; wrongType: number }
  >(functions, "dismissContacts");
  return (await fn({ inboxItemIds })).data;
}

export type DonationMethod = "cash" | "transfer" | "cheque" | "card" | "other";

/**
 * Records money that arrived outside the payment provider — cash, a transfer,
 * a cheque.
 *
 * Through the server because `donations` is writable by no client role: a
 * donation record is the basis of a receipt and of every total on the donor
 * screen, so who wrote it and when has to come from somewhere other than the
 * browser claiming it.
 */
export async function recordManualDonation(input: {
  personId: string;
  /** Agorot, integer. The sheet converts once, at the boundary where a human
   *  types a decimal. */
  amountMinor: number;
  collectedAt?: string;
  method?: DonationMethod;
  purpose?: string;
  note?: string;
}): Promise<{ donationId: string; amountMinor: number; collectedAt: string }> {
  const fn = httpsCallable<
    typeof input,
    { donationId: string; amountMinor: number; collectedAt: string }
  >(functions, "recordManualDonation");
  return (await fn(input)).data;
}

/* ------------------------------------------------------------------------- */
/* The capture box                                                            */
/* ------------------------------------------------------------------------- */

/**
 * Writing something down so it is not forgotten.
 *
 * "לגבי האינבוקס זה צריך להיות כמו שיש אינבוקס בטרלו, שכותבים שם דברים כדי שלא
 * ישכחו ואחר כך ממנינים אותם למקומם."
 *
 * ## Why this one is a direct write and almost nothing else is
 *
 * Every other mutation in this file is a callable, because it decides something:
 * who a payment belongs to, whether two records are one person, that money
 * arrived. A capture decides nothing — it is a line of text addressed to
 * yourself — and it has to land the instant it is typed. The Firestore SDK
 * applies a write to its local cache before the server has heard about it, so
 * the note appears immediately and survives a tunnel; a callable would put a
 * cold start between the thought and the note, and this product owner has
 * already described exactly that: "לוקח המון זמן לפעמים לדברים להשמר".
 *
 * Filing a note — into a task, or onto somebody's timeline — is a callable,
 * because that does create records. See `fileCapture`.
 */
export async function captureNote(text: string): Promise<string> {
  const clean = text.trim();
  if (clean === "") throw new Error("אין מה לשמור.");

  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error("צריך להתחבר מחדש.");

  const ref = await addDoc(collection(db, "captures"), {
    workspaceId: WORKSPACE_ID,
    schemaVersion: 1,
    text: clean.slice(0, 2000),
    status: "open",
    // The rule requires this to match the caller, so a note cannot be attributed
    // to somebody else.
    createdByUid: uid,
    /**
     * The list is ordered by this, not by `createdAt`, and the difference is
     * the whole feature.
     *
     * A `serverTimestamp()` is a sentinel: until the server acknowledges the
     * write, the local cache reads the field as `null`. A query ordered by it
     * descending therefore puts the note that was just typed at the **bottom**
     * of the list — which looks exactly like the note not saving, on the one
     * screen whose entire promise is that it saves instantly.
     *
     * A client clock is a poor authority in general and a perfectly good one
     * here: these are one person's notes to themselves, read in the order they
     * were written. `createdAt` is still recorded, and is still the truth.
     */
    createdAtLocal: new Date().toISOString(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    filedAt: null,
    filedBy: null,
    filedAs: null,
  });
  return ref.id;
}

/** Fixing a word in a note still sitting in the box. Dictated text needs this
 *  more often than typed text does. */
export async function editCapture(captureId: string, text: string): Promise<void> {
  const clean = text.trim();
  if (clean === "") throw new Error("אין מה לשמור.");
  await updateDoc(doc(db, "captures", captureId), {
    text: clean.slice(0, 2000),
    updatedAt: serverTimestamp(),
  });
}

/** Gone, with no trace. A line written by mistake is not a record, and the
 *  audit trail exists for filing, which is the part that creates things. */
export async function deleteCapture(captureId: string): Promise<void> {
  await deleteDoc(doc(db, "captures", captureId));
}

export type CaptureDestination = "task" | "person" | "done" | "deleted";

export interface FileCaptureResult {
  alreadyFiled: boolean;
  status: string;
  filedAs: { type: string; id: string } | null;
}

/**
 * Sorting a note to its place.
 *
 * A callable rather than a client write: filing creates a task somebody will be
 * reminded about, or an entry on a person's permanent timeline, and the note has
 * to close in the same batch. A note closed without the task it claims to have
 * created is the reminder silently thrown away — which is the thing this whole
 * feature exists to prevent.
 */
export async function fileCapture(input: {
  captureId: string;
  to: CaptureDestination;
  personId?: string;
  /** ISO instant. Required for `task`. */
  dueAt?: string;
  /** A shorter title, when the captured line is a paragraph. */
  title?: string;
}): Promise<FileCaptureResult> {
  const fn = httpsCallable<typeof input, FileCaptureResult>(functions, "fileCapture");
  return (await fn(input)).data;
}

/**
 * Giving a donation back the name it lost.
 *
 * Production holds 24 donations worth ₪9,191 with no person attached — eighteen
 * spreadsheet rows the importer could not resolve, and six whose donor record
 * was deleted afterwards. Attaching one updates the donation, the person's
 * totals and their timeline together, and is audited: this is somebody deciding
 * whose money it was on the basis of a name, and that decision should be
 * reviewable.
 */
export async function attributeDonation(
  donationId: string,
  personId: string,
): Promise<{ ok: boolean; alreadyAttributed: boolean; personId: string }> {
  const fn = httpsCallable<
    { donationId: string; personId: string },
    { ok: boolean; alreadyAttributed: boolean; personId: string }
  >(functions, "attributeDonation");
  return (await fn({ donationId, personId })).data;
}
